/**
 * Alpine component for the moderator cases page (tickets C4a/C4b, #3279).
 * Fetches the queue from GET /v1/mod/cases and each dossier from
 * GET /v1/mod/cases/{id} via window.ApiClient. Decisions post to
 * POST /v1/mod/cases/{id}/decision carrying the case version the pane
 * rendered; a 409 means the case moved (new evidence or a concurrent
 * decision) and the pane reloads for a fresh decision. Overturn posts to
 * POST /v1/mod/cases/{id}/overturn.
 *
 * All ids are strings (case and subject ids exceed Number.MAX_SAFE_INTEGER);
 * they are never parsed as numbers. Lucide icons in the dynamically rendered
 * detail are re-initialised after each render.
 */

import {
    describeBareBlockFilings,
    fetchFilingSummary,
    reportAbuseWarnAdvisory,
} from '../modules/report-abuse-advisory.js';

const LANE_LABELS = {
    low_confidence: 'Low-confidence lane',
    general: 'General lane',
};

const RESOLUTION_LABELS = {
    delete: 'Deleted',
    warn: 'Warned',
    suspend: 'Suspended',
    delete_photo: 'Photo removed',
    dismiss: 'Dismissed',
    deleted_externally: 'Gone',
    overturned: 'Overturned',
};

const ACTOR_LABELS = {
    automod: 'Automod',
    moderator: 'Moderator',
    system: 'System',
};

const STATE_LABELS = {
    needs_automation: 'Awaiting automod',
    needs_human: 'Needs a human',
    deferred: 'Deferred',
    resolved: 'Resolved',
};

/** Automod verdict `action` values (report verdicts and user scans alike). */
const VERDICT_ACTION_LABELS = {
    approve: 'Approved',
    log: 'Flagged for review',
    flag: 'Flagged for review',
    none: 'No action',
    delete: 'Deleted',
    warn: 'Warned',
    suspend: 'Suspended',
    restore: 'Restored',
};

/** Verdict `source` values, as provenance chips. */
const VERDICT_SOURCE_LABELS = {
    'llm': 'LLM verdict',
    'llm-error': 'LLM error',
    'llm-held': 'Verdict held by guard',
    'llm-shadow': 'Shadow verdict',
    'omni-moderation': 'Classifier',
    'skip:prior_approve': 'Prior-approve skip',
    'abuse-gate': 'Reporter-abuse gate',
    'skip:mod_role': 'Staff-account skip',
};

const FLAG_TYPE_LABELS = {
    photo_nsfw: 'NSFW photo',
    acct_ip_mismatch: 'IP / country mismatch',
    acct_device_cluster: 'Shared-device cluster',
    acct_cookie: 'Shared signup cookie',
    text_rule_match: 'Text-rule match',
    photo_ai_generated: 'AI-generated photo',
};

/**
 * Headlines for the photo-hash gate's key=value coverage comments
 * (`reason=no_match gate_action=clean hamming_distance=0`), keyed by the
 * gate's `reason` token. The raw pairs stay available as chips.
 */
const GATE_REASON_SUMMARIES = {
    no_match: 'Avatar checked against the banned scammer-photo list: no match.',
    matched: 'Avatar matches a banned scammer photo.',
    minor: 'Matched a banned photo, but the subject is a minor. Degraded to flag for review.',
    established: 'Matched a banned photo, but the account is established. Degraded to flag for review.',
    collision: 'Matched a banned photo shared with another active account (likely a stock image). Flag only.',
    flag_intent: 'Matched a banned photo whose ban intent is flag-for-review.',
    delete_photo_intent: 'Matched a banned photo carrying a photo-only ban.',
    kill_switch: 'Gate self-disabled (ban list over its ceiling). No check performed.',
    immune: 'Subject has automod immunity. Gate skipped.',
    encoding_suspect: 'Avatar has no usable perceptual hash. Gate skipped.',
};

const KV_KEY_LABELS = {
    reason: 'reason',
    gate_action: 'gate action',
    hamming_distance: 'hamming distance',
    matched_photo_id: 'matched photo',
    matched_source_user_id: 'matched account',
    ban_source: 'ban source',
    intended_action: 'intended action',
    colliding_user_id: 'colliding account',
};

/**
 * The endpoint's ceiling, applied to each bucket. There is no paging, so a
 * bucket past this is reported as a total the page cannot show rows for.
 */
const QUEUE_LIMIT = 200;

/**
 * The modern warning id whose issuance arms the 90-day reporter abuse gate.
 * Mirrors ReporterReputationGateway::ABUSE_GATE_REPORT_ABUSE_WARNING_ID (a JS
 * module cannot import a PHP constant); the mirror is pinned by
 * tests/unit/Helpers/ReportAbuseWarnInterstitialMirrorTest.php.
 */
const REPORT_ABUSE_WARNING_ID = 14;

// Filing-profile sentences and the summary lookup are single-sourced in the
// shared advisory module, imported here and read through a window bridge by
// the jQuery surface in moderate.js.

/**
 * Pending-execution poll: after an async decision (a delete) the case comes
 * back is_pending, and the server resolves it within seconds (the mod delete
 * queue drains on a ~5s tick). The dossier pane refetches on this interval
 * until the case settles, capped so a genuinely stuck execution is left to
 * reconciliation rather than spinning forever.
 *
 * Each tick refetches the full dossier. That is deliberate: the pending
 * window is normally one or two ticks, only the case a moderator is actively
 * viewing polls, and a dedicated lightweight status endpoint is not worth its
 * surface for a bounded worst case of PENDING_POLL_MAX builds.
 */
const PENDING_POLL_MS = 5000;
const PENDING_POLL_MAX = 60;

// Decision notices derived from case state. A delete can finish executing
// before its POST even returns, and a stranded execution can release the
// case back to the queue, so "not pending" is not synonymous with resolved.
const NOTICE_DECISION_QUEUED = 'Deletion queued. The case resolves when it completes.';
const NOTICE_DECISION_EXECUTED = 'Decision executed. The case is resolved.';
const NOTICE_EXECUTION_RETURNED = 'Execution did not complete. The case is back in the queue.';

const AVATAR_GRADIENTS = [
    'linear-gradient(135deg,#60a5fa,#2563eb)',
    'linear-gradient(135deg,#f472b6,#c026d3)',
    'linear-gradient(135deg,#fbbf24,#f97316)',
    'linear-gradient(135deg,#34d399,#059669)',
    'linear-gradient(135deg,#a78bfa,#7c3aed)',
    'linear-gradient(135deg,#f9a8d4,#db2777)',
    'linear-gradient(135deg,#94a3b8,#64748b)',
];

export function modCasesPage(config = {}) {
    return {
        preselectCaseId: String(config.preselectCaseId || ''),

        loading: true,
        loadError: '',
        needsHuman: [],
        deferred: [],
        resolvedRecent: [],
        // How many decidable cases each open bucket holds in full. The lists are
        // capped at `limit` rows each, so these are what the counts must report:
        // a moderator sizing up the backlog needs the backlog, not the page.
        totals: { needs_human: 0, deferred: 0 },
        limit: 0,
        filter: 'all',

        selectedId: '',
        detail: null,
        detailLoading: false,
        detailError: '',
        showDetailMobile: false,

        // Pending-execution poll timer + guards (see PENDING_POLL_MS). Track the
        // silent refetch loop that swaps in the resolved dossier once an async
        // decision (a delete) finishes executing on the server.
        pollTimer: null,
        pollAttempts: 0,
        pollBusy: false,
        // Poll generation, bumped by stopPending(). A tick whose fetch
        // outlives its own poll (a slow response spanning a same-case
        // re-select) must not unlatch pollBusy for the generation that
        // replaced it.
        pollGen: 0,

        // Monotonic request tokens. Concurrent fetches of the same case (an
        // explicit re-select racing an in-flight silent poll) and overlapping
        // queue reloads can resolve in any order; only the newest request may
        // write state, so an older response never clobbers a newer dossier or
        // queue snapshot.
        detailFetchSeq: 0,
        queueLoadSeq: 0,

        decisionBusy: false,
        // Raised for the report-abuse warn advisory's profile lookup and
        // modal. Separate from decisionBusy deliberately: postCase() refuses
        // to run while decisionBusy is set, so the advisory's confirm
        // callback could never post if it shared the flag. The warn button
        // disables on either.
        warnAdvisoryPending: false,
        decisionError: '',
        decisionNotice: '',
        openForm: '',
        decisionNote: '',
        deferWakeAt: '',
        warnWarningId: '',
        suspendReason: '',
        suspendDays: 7,
        deleteReason: '',
        deleteAgeAck: false,
        overturnReason: '',

        init() {
            this.loadQueue();
            this.sizeShell();
            this.onResize = () => this.sizeShell();
            window.addEventListener('resize', this.onResize);
        },

        destroy() {
            this.stopPending();
            if (this.onResize) {
                window.removeEventListener('resize', this.onResize);
                this.onResize = null;
            }
        },

        /**
         * Size the split-pane shell to the exact space below the page chrome,
         * so the panes reach the bottom of the viewport regardless of how tall
         * the site header and mod nav are. A static calc() has to guess the
         * chrome height and either overshoots (dead band at the bottom) or
         * undershoots (page scrollbar); measuring doesn't.
         */
        sizeShell() {
            const shell = this.$root;
            if (!shell) {
                return;
            }
            if (!window.matchMedia('(min-width: 1024px)').matches) {
                shell.style.height = '';
                return;
            }
            // Viewport-relative top: the pane fills from its current position
            // to the viewport bottom regardless of any restored scroll offset.
            const top = shell.getBoundingClientRect().top;
            shell.style.height = Math.max(320, window.innerHeight - top) + 'px';
        },

        /**
         * `silent` skips raising the loading flag (which unmounts the whole
         * queue list while the request runs), so a background refresh (the
         * pending-execution poll settling a case) swaps the rows in place
         * instead of flashing the pane. A silent failure keeps the current
         * list rather than surfacing a transient error.
         */
        async loadQueue(silent = false) {
            const seq = ++this.queueLoadSeq;
            if (!silent) {
                this.loading = true;
                this.loadError = '';
            }
            try {
                const response = await window.ApiClient.request('/mod/cases?limit=' + QUEUE_LIMIT);
                if (seq !== this.queueLoadSeq) {
                    return;
                }
                if (!response.ok) {
                    if (!silent) {
                        this.loadError = 'Could not load the cases queue (' + response.status + ').';
                    }
                    return;
                }
                const body = await response.json();
                if (seq !== this.queueLoadSeq) {
                    return;
                }
                // Any successful current response unblinds the list: a silent
                // refresh must also clear an error a failed manual load left,
                // or the fresh rows stay hidden behind the error pane.
                this.loadError = '';
                this.needsHuman = body.needs_human || [];
                this.deferred = body.deferred || [];
                this.resolvedRecent = body.resolved_recent || [];
                this.limit = body.limit ?? 0;
                this.totals = {
                    needs_human: body.totals?.needs_human ?? this.needsHuman.length,
                    deferred: body.totals?.deferred ?? this.deferred.length,
                };

                if (this.preselectCaseId) {
                    const target = this.preselectCaseId;
                    this.preselectCaseId = '';
                    this.selectCase(target);
                }
            } catch (_e) {
                if (seq === this.queueLoadSeq && !silent) {
                    this.loadError = 'Could not load the cases queue.';
                }
            } finally {
                // The newest reload owns the spinner: a silent refresh never
                // raises it, but still lowers one left by a manual load it
                // superseded (that load's own clear is seq-guarded away).
                if (seq === this.queueLoadSeq) {
                    this.loading = false;
                }
            }
        },

        get totalOpen() {
            return this.totals.needs_human + this.totals.deferred;
        },

        /**
         * The header shown above a bucket's rows. Says so plainly when the list
         * is a capped page of a larger bucket, so a moderator looking at 50 rows
         * knows whether that is the whole backlog or the top of it.
         */
        bucketHeading(title, shown, total) {
            return total > shown ? title + ' (' + shown + ' of ' + total + ')' : title;
        },

        /**
         * The resolved bucket is a bounded history browser with no total behind
         * it, so a full page means "at least this many", not "this many".
         */
        get resolvedCountLabel() {
            const shown = this.resolvedRecent.length;
            return this.limit > 0 && shown >= this.limit ? shown + '+' : String(shown);
        },

        get visibleNeedsHuman() {
            return this.filter === 'all' || this.filter === 'needs' ? this.needsHuman : [];
        },

        get visibleDeferred() {
            return this.filter === 'all' || this.filter === 'deferred' ? this.deferred : [];
        },

        get visibleResolved() {
            return this.filter === 'all' || this.filter === 'resolved' ? this.resolvedRecent : [];
        },

        get isEmpty() {
            return this.needsHuman.length === 0
                && this.deferred.length === 0
                && this.resolvedRecent.length === 0;
        },

        get filteredEmpty() {
            return !this.isEmpty
                && this.visibleNeedsHuman.length === 0
                && this.visibleDeferred.length === 0
                && this.visibleResolved.length === 0;
        },

        setFilter(value) {
            this.filter = value;
        },

        async selectCase(id) {
            const caseId = String(id);
            // Selecting a different case clears the prior case's pending poll
            // before the new fetch, so a stale timer never keeps refetching the
            // case the moderator just left.
            this.stopPending();
            this.selectedId = caseId;
            this.showDetailMobile = true;
            this.detail = null;
            this.detailError = '';
            this.decisionError = '';
            this.decisionNotice = '';
            this.resetDecisionForms();
            this.detailLoading = true;
            // fetchDetail claims startSeq + 1. A same-case re-select mid-flight
            // claims a later token and owns the spinner from then on, so the
            // superseded call must not clear it while `detail` is still null.
            const startSeq = this.detailFetchSeq;
            try {
                await this.fetchDetail(caseId, false);
            } finally {
                if (this.selectedId === caseId && this.detailFetchSeq === startSeq + 1) {
                    this.detailLoading = false;
                }
            }
            // Poll while the freshly loaded case is still executing an async
            // decision (a delete). No-op for any already-settled case.
            if (this.selectedId === caseId) {
                this.maybeStartPending();
            }
        },

        /**
         * Fetch the dossier for `caseId` and swap it into `detail`. Shared by the
         * initial select and the silent pending-execution poll. Stale-guards on
         * `selectedId` (a response for a case the moderator has already navigated
         * away from never clobbers the current pane: decisions read the case id
         * and version from `detail`) and on `detailFetchSeq`, so when two fetches
         * of the SAME case race (a silent poll in flight while a re-select
         * fetches fresh), the older response is discarded instead of overwriting
         * the newer dossier with a stale snapshot.
         *
         * `silent` is the poll path. It leaves `detailLoading`, `detailError`,
         * and scroll position untouched and only swaps `detail` on success, so a
         * background refresh never flashes the spinner or surfaces a transient
         * error over a case that is still on screen. Silent refetches default
         * to the light dossier — the server skips the evidence sweep, photo
         * strip, and subject investigation block that state tracking does not
         * need — and keep those blocks already on screen. Pass `light: false`
         * with `silent` for a full-payload refresh without the spinner (the
         * post-execution reconcile).
         */
        async fetchDetail(caseId, silent, light = silent) {
            const seq = ++this.detailFetchSeq;
            try {
                const url = '/mod/cases/' + encodeURIComponent(caseId) + (light ? '?light=1' : '');
                const response = await window.ApiClient.request(url);
                if (seq !== this.detailFetchSeq || this.selectedId !== caseId) {
                    return;
                }
                if (!response.ok) {
                    if (!silent) {
                        this.detailError = response.status === 404
                            ? 'This case no longer exists.'
                            : 'Could not load this case (' + response.status + ').';
                    }
                    return;
                }
                const body = await response.json();
                if (seq !== this.detailFetchSeq || this.selectedId !== caseId) {
                    return;
                }
                if (light && this.detail) {
                    // The light dossier empties these blocks: keep the ones
                    // already on screen. Case, transitions, and decision
                    // fields come fresh from the poll response.
                    if (!body.subject_detail && this.detail.subject_detail) {
                        body.subject_detail = this.detail.subject_detail;
                    }
                    body.evidence = this.detail.evidence;
                    body.photos = this.detail.photos;
                }
                this.detail = body;
                this.primeDecisionDefaults();
                this.refreshIcons();
            } catch (_e) {
                if (!silent && seq === this.detailFetchSeq && this.selectedId === caseId) {
                    this.detailError = 'Could not load this case.';
                }
            }
        },

        /**
         * (Re)start the pending-execution poll for the loaded case. Idempotent:
         * always clears a prior timer first, so a re-select or a fresh decision
         * never stacks intervals. Starts a timer only while the loaded case is
         * still executing.
         */
        maybeStartPending() {
            this.stopPending();
            if (this.detail && this.detail.case && this.detail.case.is_pending) {
                this.pollTimer = window.setInterval(() => this.pollPending(), PENDING_POLL_MS);
            }
        },

        stopPending() {
            if (this.pollTimer) {
                window.clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
            this.pollGen += 1;
            this.pollAttempts = 0;
            this.pollBusy = false;
        },

        /**
         * One poll tick: silently refetch the executing case. When it has
         * resolved, stop and reload the queue so the left list moves the row out
         * of the open buckets. Skips a tick while a prior refresh is still in
         * flight, and gives up after PENDING_POLL_MAX attempts — a genuinely
         * stuck execution is reconciliation's job, not this loop's.
         */
        async pollPending() {
            if (this.pollBusy) {
                return;
            }
            if (this.pollAttempts >= PENDING_POLL_MAX) {
                this.stopPending();
                // The loop is bounded on purpose (a genuinely stuck execution
                // is reconciliation's job). Say so instead of leaving the
                // "Executing" pill spinning with no explanation.
                this.decisionNotice = 'Still executing after several minutes. Re-open the case to check again.';
                return;
            }
            this.pollAttempts += 1;
            this.pollBusy = true;
            const gen = this.pollGen;
            const caseId = this.selectedId;
            try {
                await this.fetchDetail(caseId, true);
            } finally {
                // Release the guard only within the generation that latched it;
                // a case switch or poll restart mid-request already reset the
                // state for the next generation, and a stale tick must not
                // unlatch a newer in-flight refresh.
                if (gen === this.pollGen) {
                    this.pollBusy = false;
                }
            }
            if (gen !== this.pollGen || this.selectedId !== caseId) {
                return;
            }
            if (!this.detail || !this.detail.case || !this.detail.case.is_pending) {
                this.stopPending();
                // Light polls carry the on-screen evidence/photo/subject
                // blocks forward. Execution can change all three (deleted
                // photos, a removed account): reconcile once with a full
                // payload, without flashing the spinner. A failed reconcile
                // keeps the settled light dossier; re-opening the case
                // reloads fresh.
                await this.fetchDetail(caseId, true, false);
                if (this.selectedId !== caseId) {
                    return;
                }
                if (this.detail && this.detail.case && this.detail.case.is_pending) {
                    // A concurrent decision re-queued execution between the
                    // poll tick and the reconcile: resume watching instead of
                    // stranding the "Executing" pill with no poll.
                    this.maybeStartPending();
                    return;
                }
                // The "queued, resolves when it completes" notice from the
                // decision submit is stale once the case has settled. Not
                // pending does not always mean resolved: a stranded execution
                // releases the claim and the case returns to the queue.
                if (this.detail && this.detail.case) {
                    this.decisionNotice = this.detail.case.state === 'resolved'
                        ? NOTICE_DECISION_EXECUTED
                        : NOTICE_EXECUTION_RETURNED;
                }
                this.loadQueue(true);
            }
        },

        /**
         * Seed form defaults from the loaded dossier's decision options, so
         * selects never sit on an empty value.
         */
        primeDecisionDefaults() {
            const options = this.decisionOptions;
            if (!this.overturnReason && (options.restore_reasons || []).length) {
                this.overturnReason = options.restore_reasons[0];
            }
        },

        backToList() {
            this.stopPending();
            this.showDetailMobile = false;
            this.selectedId = '';
        },

        // ── Decisions (C4b) ──────────────────────────────────────────────

        get caseOpen() {
            return !!(this.detail && this.detail.case && this.detail.case.state !== 'resolved');
        },

        get canDecideNow() {
            return !!(this.detail && this.detail.can_decide && this.caseOpen && !this.detail.case.is_pending);
        },

        get canOverturnNow() {
            // can_overturn is server-computed: resolved automod deletes only.
            // A human delete is not overturnable from this surface.
            return !!(this.detail && this.detail.can_decide && this.detail.can_overturn);
        },

        get minDeferDate() {
            const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
            return tomorrow.toISOString().slice(0, 10);
        },

        get decisionOptions() {
            return (this.detail && this.detail.decision_options) || {};
        },

        toggleForm(kind) {
            this.decisionError = '';
            if (this.openForm === kind) {
                this.openForm = '';
                return;
            }
            this.openForm = kind;
            const options = this.decisionOptions;
            if (kind === 'warn' && !this.warnWarningId && (options.warnings || []).length) {
                this.warnWarningId = String(options.warnings[0].id);
            }
            if (kind === 'suspend') {
                if (!this.suspendReason) {
                    this.suspendReason = (options.suspend_reasons || [])[0] || '';
                }
                this.suspendDays = options.suspend_days_default || 7;
            }
            if (kind === 'delete' && !this.deleteReason) {
                const reasons = options.delete_reasons || [];
                this.deleteReason = reasons.indexOf('fake') !== -1 ? 'fake' : (reasons[0] || '');
            }
            if (kind === 'overturn' && !this.overturnReason) {
                this.overturnReason = (options.restore_reasons || [])[0] || '';
            }
            this.refreshIcons();
        },

        resetDecisionForms() {
            this.openForm = '';
            this.warnAdvisoryPending = false;
            this.decisionNote = '';
            this.deferWakeAt = '';
            this.warnWarningId = '';
            this.suspendReason = '';
            this.suspendDays = 7;
            this.deleteReason = '';
            this.deleteAgeAck = false;
            this.overturnReason = '';
        },

        subjectHandle() {
            const subject = (this.detail && this.detail.subject) || {};
            return subject.username ? '@' + subject.username : ('#' + (subject.id || ''));
        },

        async postCase(path, payload, notice) {
            if (this.decisionBusy || !this.detail) {
                return;
            }
            this.decisionBusy = true;
            this.decisionError = '';
            this.decisionNotice = '';
            const caseId = String(this.detail.case.id);
            try {
                const response = await window.ApiClient.request(
                    '/mod/cases/' + encodeURIComponent(caseId) + path,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    },
                );
                if (response.status === 409) {
                    // The moderator may have navigated to another case while
                    // this was in flight: don't hijack the pane back.
                    if (String(this.selectedId) !== caseId) {
                        return;
                    }
                    let conflictCode = '';
                    try {
                        conflictCode = String((await response.json()).error.code || '');
                    } catch {
                        conflictCode = '';
                    }
                    // selectCase clears decisionError, so set the explanation
                    // after the reload or the moderator never sees it.
                    await this.selectCase(caseId);
                    this.decisionError = conflictCode === 'case_tenure_ack_required'
                        ? 'This account is well-established. Confirm the account-age acknowledgement first.'
                        : 'This case changed while you were looking at it. Reloaded, decide again.';
                    this.loadQueue();
                    return;
                }
                if (!response.ok) {
                    // A late failure for a case the moderator has since left
                    // must not surface an error over the pane they moved to.
                    if (String(this.selectedId) !== caseId) {
                        return;
                    }
                    this.decisionError = 'Decision failed (' + response.status + ').';
                    return;
                }
                const updated = await response.json();
                // A slower decision response for a case the moderator has since
                // navigated away from must not clobber the current pane.
                if (String(this.selectedId) !== caseId) {
                    return;
                }
                // The decision response is authoritative: invalidate any dossier
                // fetch still in flight (a re-select racing this POST) so a
                // pre-decision snapshot cannot overwrite this write afterwards.
                // That superseded fetch no longer clears the spinner its
                // selectCase raised, so settle it here too.
                this.detailFetchSeq += 1;
                this.detail = updated;
                this.detailLoading = false;
                // A delete executes asynchronously and comes back is_pending, so
                // poll until the pane flips to resolved on its own.
                this.maybeStartPending();
                // Trust the returned dossier over the queued-flavor wording
                // when the execution already settled server-side. Same state
                // derivation as the poll: not-pending is not synonymous with
                // resolved (a released claim returns the case to the queue).
                this.decisionNotice = notice === NOTICE_DECISION_QUEUED
                        && updated.case && !updated.case.is_pending
                    ? (updated.case.state === 'resolved'
                        ? NOTICE_DECISION_EXECUTED
                        : NOTICE_EXECUTION_RETURNED)
                    : notice;
                this.resetDecisionForms();
                this.loadQueue();
                this.refreshIcons();
            } catch (_e) {
                if (String(this.selectedId) !== caseId) {
                    return;
                }
                this.decisionError = 'Decision failed. Check your connection and retry.';
            } finally {
                this.decisionBusy = false;
            }
        },

        decisionPayload(resolution, extra) {
            const payload = Object.assign({
                version: this.detail.case.version,
                resolution,
            }, extra || {});
            const note = this.decisionNote.trim();
            if (note !== '') {
                payload.note = note;
            }
            return payload;
        },

        decideDismiss() {
            this.postCase('/decision', this.decisionPayload('dismiss'), 'Case dismissed.');
        },

        decideDefer() {
            const extra = {};
            if (this.deferWakeAt) {
                extra.wake_at = this.deferWakeAt + ' 00:00:00';
            }
            this.postCase('/decision', this.decisionPayload('defer', extra), 'Case deferred.');
        },

        async decideWarn() {
            if (this.decisionBusy || this.warnAdvisoryPending) {
                return;
            }
            if (!this.warnWarningId) {
                this.decisionError = 'Pick a warning first.';
                return;
            }
            // "Report System Abuse" carries consequences the label does not
            // say: for 90 days the reporter abuse gate changes how the warned
            // user's reports resolve, and bare block-and-report volume is
            // automod-only signal (#3860), so volume alone is not abuse.
            // Interpose an advisory carrying both facts plus the subject's
            // filing profile, mirroring the legacy mod-action surface in
            // moderate.js. A missing ConfirmModal falls through to the plain
            // send, matching that surface's pass-through: the advisory is
            // advisory, and a broken modal must not eat the warn.
            if (String(this.warnWarningId) === String(REPORT_ABUSE_WARNING_ID)
                && window.ConfirmModal) {
                // The pane stays navigable while the profile lookup is in
                // flight, so everything the confirm acts on is pinned here:
                // the warning id (the select can change) and the case (the
                // moderator can click another queue row). The confirm must
                // send the warning the moderator read about to the case they
                // read it about, so any drift cancels instead of sending.
                const warningId = this.warnWarningId;
                const caseId = String((((this.detail || {}).case || {}).id) || '');
                const subjectId = String(((this.detail || {}).subject || {}).id || '');
                const caseUnchanged = () =>
                    String((((this.detail || {}).case || {}).id) || '') === caseId;
                const send = () => {
                    if (!caseUnchanged()) {
                        console.warn('report-abuse warn advisory: case changed while open, not sending');
                        return;
                    }
                    this.postCase(
                        '/decision',
                        this.decisionPayload('warn', { warning_id: warningId }),
                        'Warning sent. Case resolved.',
                    );
                };

                // Guards re-entry across the await: a second click in that
                // window would run the whole path again and stack a second
                // modal, and each ConfirmModal.show() takes a body scroll
                // lock that close() releases exactly once.
                this.warnAdvisoryPending = true;
                let detailLine = '';
                try {
                    detailLine = describeBareBlockFilings(
                        subjectId !== '' ? await fetchFilingSummary(subjectId) : null
                    );
                } catch (err) {
                    console.warn('report-abuse warn advisory failed, passing through:', err);
                    this.warnAdvisoryPending = false;
                    send();
                    return;
                }
                if (!caseUnchanged()) {
                    // The stats describe the case that was open at the click;
                    // showing them over a different dossier would pair one
                    // subject's advisory with another's warn. The moderator
                    // re-clicks warn on the case they mean.
                    console.warn('report-abuse warn advisory: case changed during lookup, not showing');
                    this.warnAdvisoryPending = false;
                    return;
                }

                window.ConfirmModal.show(reportAbuseWarnAdvisory(
                    detailLine,
                    'Warn ' + this.subjectHandle() + ' for report abuse?',
                    () => {
                        this.warnAdvisoryPending = false;
                        send();
                    },
                    () => {
                        this.warnAdvisoryPending = false;
                    },
                ));
                return;
            }
            this.postCase(
                '/decision',
                this.decisionPayload('warn', { warning_id: this.warnWarningId }),
                'Warning sent. Case resolved.',
            );
        },

        decideSuspend() {
            if (this.decisionBusy) {
                return;
            }
            if (!this.suspendReason) {
                this.decisionError = 'Pick a reason first.';
                return;
            }
            const minDays = Number(this.decisionOptions.suspend_days_min) || 1;
            const maxDays = Number(this.decisionOptions.suspend_days_max) || 30;
            const days = Number(this.suspendDays);
            if (this.suspendDays === '' || this.suspendDays === null
                || !Number.isInteger(days) || days < minDays || days > maxDays) {
                this.decisionError = 'Suspension length must be between ' + minDays + ' and ' + maxDays + ' days.';
                return;
            }
            window.ConfirmModal.show({
                title: 'Suspend account',
                message: 'Suspend ' + this.subjectHandle() + ' for ' + this.suspendDays + ' days?',
                confirmText: 'Suspend',
                type: 'warning',
                onConfirm: () => this.postCase(
                    '/decision',
                    this.decisionPayload('suspend', {
                        reason_category: this.suspendReason,
                        duration_days: this.suspendDays,
                    }),
                    'Suspension applied. Case resolved.',
                ),
            });
        },

        decideDelete() {
            if (this.decisionBusy) {
                return;
            }
            if (!this.deleteReason) {
                this.decisionError = 'Pick a reason first.';
                return;
            }
            const tenure = this.detail && this.detail.delete_tenure;
            if (tenure) {
                if (!this.deleteAgeAck) {
                    this.decisionError = 'Confirm you understand this account is well-established.';
                    return;
                }
                if (tenure.requires_note && this.decisionNote.trim() === '') {
                    this.decisionError = 'Add a note explaining the deletion.';
                    return;
                }
            }
            const extra = { reason_category: this.deleteReason };
            if (tenure && this.deleteAgeAck) {
                extra.account_age_ack = true;
            }
            window.ConfirmModal.show({
                title: 'Delete account',
                message: 'Delete ' + this.subjectHandle() + '? The account is restorable for about a year'
                    + ' and the appeal window stays open.',
                confirmText: 'Delete account',
                type: 'danger',
                onConfirm: () => this.postCase(
                    '/decision',
                    this.decisionPayload('delete', extra),
                    NOTICE_DECISION_QUEUED,
                ),
            });
        },

        decideOverturn() {
            if (this.decisionBusy) {
                return;
            }
            const note = this.decisionNote.trim();
            if (note === '') {
                this.decisionError = 'An overturn needs a note saying why automod got it wrong.';
                return;
            }
            if (!this.overturnReason) {
                this.decisionError = 'Pick a restore reason first.';
                return;
            }
            window.ConfirmModal.show({
                title: 'Overturn deletion',
                message: 'Restore ' + this.subjectHandle() + ' and mark this case overturned?'
                    + ' The account gets automod immunity so the same signals do not re-delete it.',
                confirmText: 'Overturn',
                type: 'warning',
                onConfirm: () => this.postCase(
                    '/overturn',
                    { reason_category: this.overturnReason, note },
                    'Deletion overturned. Account restored.',
                ),
            });
        },

        isSelected(id) {
            return this.selectedId !== '' && String(id) === this.selectedId;
        },

        refreshIcons() {
            this.$nextTick(() => {
                if (window.LucideIcons && this.$refs.detailHost) {
                    window.LucideIcons.initialize(this.$refs.detailHost);
                }
            });
        },

        // ── Presentation helpers ──────────────────────────────────────────

        laneLabel(lane) {
            return LANE_LABELS[lane] || 'General lane';
        },

        stateLabel(state) {
            return STATE_LABELS[state] || state || '';
        },

        verdictActionLabel(action) {
            return VERDICT_ACTION_LABELS[action] || action || '';
        },

        /**
         * Provenance chip for an automod verdict. Gate rows carry their gate
         * tag in the source (user scans) or model (legacy rows) slot
         * (`gate:photo_scammer_ban`); LLM rows carry the model name; the source
         * column disambiguates the rest.
         */
        verdictSourceLabel(entry) {
            if (this.isGateEntry(entry)) {
                return 'Photo-hash gate';
            }
            // Unmapped sources show their raw token: provenance beats a
            // hidden chip when a new writer appears before this map learns it.
            return VERDICT_SOURCE_LABELS[entry.source] || entry.source || '';
        },

        isGateEntry(entry) {
            return String(entry.source || '').indexOf('gate:') === 0
                || String(entry.model || '').indexOf('gate:') === 0;
        },

        /** True for machine-formatted `key=value key=value` comments. */
        isKvComment(comment) {
            return /^[a-z0-9_]+=\S+(\s+[a-z0-9_]+=\S+)*$/i.test(String(comment || '').trim());
        },

        /**
         * Parse a `key=value` comment into [{key, label, value}] rows for chip
         * rendering, with friendly key names where we know them.
         */
        parseKvComment(comment) {
            const pairs = [];
            String(comment || '').trim().split(/\s+/).forEach((token) => {
                const eq = token.indexOf('=');
                if (eq < 1) {
                    return;
                }
                const key = token.slice(0, eq);
                const value = token.slice(eq + 1);
                pairs.push({
                    key,
                    label: KV_KEY_LABELS[key] || key.replace(/_/g, ' '),
                    value: value.replace(/_/g, ' '),
                });
            });
            return pairs;
        },

        /**
         * One-line headline for a gate coverage comment, derived from its
         * `reason=` token. Empty when the reason is unknown (chips still show).
         */
        gateSummary(comment) {
            const reasonPair = this.parseKvComment(comment).find((p) => p.key === 'reason');
            if (!reasonPair) {
                return '';
            }
            return GATE_REASON_SUMMARIES[reasonPair.value.replace(/ /g, '_')] || '';
        },

        /** The report an automod verdict ruled on, from the loaded dossier. */
        reportForVerdict(entry) {
            const reports = (this.detail && this.detail.evidence && this.detail.evidence.reports) || [];
            return reports.find((r) => String(r.report_id) === String(entry.report_id)) || null;
        },

        scrollToReport(reportId) {
            const el = document.getElementById('case-report-' + String(reportId));
            if (!el) {
                return;
            }
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ring-2', 'ring-blue-400');
            setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400'), 1600);
        },

        flagTypeLabel(type) {
            return FLAG_TYPE_LABELS[type] || String(type || '').replace(/_/g, ' ');
        },

        /**
         * Headline for one case-history row. Content actions and no-op state
         * echoes read as what happened ("Photo removed by Automod"), not as a
         * raw `needs_human → needs_human` arrow.
         */
        transitionTitle(t) {
            if (!t.from_state) {
                return 'Case opened · ' + this.stateLabel(t.to_state).toLowerCase();
            }
            if (t.detail === 'content_action') {
                return this.contentActionTitle(t);
            }
            switch (t.detail) {
                case 'overturn':
                    return 'Deletion overturned';
                case 'pending_claim':
                    return 'Decision claimed · executing';
                case 'claim_released':
                    return 'Decision claim released';
                case 'claim_released_stranded':
                    return 'Stranded decision claim released';
                case 'action_log_attached':
                    return 'Action log attached';
                case 'lane_promoted':
                    return 'Promoted to a higher-priority lane';
                default:
                    break;
            }
            if (t.from_state === t.to_state) {
                if (t.detail === 'automation_evaluated') {
                    return 'Automod re-evaluated the case';
                }
                return this.stateLabel(t.to_state) + ' · updated';
            }
            if (t.to_state === 'resolved') {
                return 'Resolved';
            }
            return this.stateLabel(t.from_state) + ' → ' + this.stateLabel(t.to_state);
        },

        contentActionTitle(t) {
            const action = t.action || null;
            if (action && action.kind === 'delete' && action.content_type) {
                const noun = action.content_type.charAt(0).toUpperCase() + action.content_type.slice(1);
                return noun + ' removed';
            }
            if (action && action.kind) {
                return (VERDICT_ACTION_LABELS[action.kind] || action.kind)
                    + (action.content_type ? ' · ' + action.content_type : '');
            }
            return t.note || 'Content action';
        },

        /** Copy helper for the subject-detail chips (email, uid). */
        async copyText(text, event) {
            // currentTarget is nulled once the event finishes dispatching, so
            // grab it before awaiting the clipboard write.
            const target = event && event.currentTarget;
            try {
                await navigator.clipboard.writeText(String(text));
            } catch {
                return;
            }
            if (target) {
                // Important-modified utilities so the flash outranks the
                // buttons' own hover: and dark: text colors in both themes
                // (the pointer is still on the button right after the click).
                target.classList.add('!text-green-600', 'dark:!text-green-400');
                setTimeout(() => target.classList.remove('!text-green-600', 'dark:!text-green-400'), 900);
            }
        },

        ipLookupUrl(ip) {
            return 'https://www.ip2location.com/' + encodeURIComponent(ip);
        },

        resolutionLabel(resolution) {
            return RESOLUTION_LABELS[resolution] || 'Resolved';
        },

        actorLabel(actor) {
            return ACTOR_LABELS[actor] || 'System';
        },

        reasonLabel(reason) {
            return String(reason || '').replace(/-/g, ' ');
        },

        avatarGradient(uid) {
            const key = String(uid || '');
            let sum = 0;
            for (let i = 0; i < key.length; i += 1) {
                sum += key.charCodeAt(i);
            }
            return AVATAR_GRADIENTS[sum % AVATAR_GRADIENTS.length];
        },

        initial(subject) {
            const name = (subject && (subject.username || subject.name)) || '?';
            return name.charAt(0).toUpperCase();
        },

        /**
         * `subject.sex` carries the raw users.sex enum, whose values are the
         * words 'male' and 'female'.
         */
        sexSymbol(sex) {
            if (sex === 'female') {
                return '♀';
            }
            if (sex === 'male') {
                return '♂';
            }
            return '';
        },

        /**
         * Right-hand state pill for a list row. Returns { label, cls } where cls
         * is the Tailwind colour set for the badge.
         */
        stateBadge(item) {
            if (item.state === 'needs_human') {
                return {
                    label: 'Decide',
                    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
                };
            }
            if (item.state === 'deferred') {
                return {
                    label: 'Deferred',
                    cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
                };
            }
            // Only a resolved case carries a resolution. The queue ships no
            // other unresolved state, so this guards the fallthrough rather than
            // a reachable row: without it an unresolved case would take the
            // resolution badge and read RESOLVED, in green, undecided.
            if (item.state !== 'resolved') {
                return {
                    label: 'Triaging',
                    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
                };
            }
            return this.resolutionBadge(item.resolution);
        },

        resolutionBadge(resolution) {
            const label = (this.resolutionLabel(resolution) || 'Resolved').toUpperCase();
            let cls = 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
            if (resolution === 'delete') {
                cls = 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
            } else if (resolution === 'warn' || resolution === 'suspend' || resolution === 'delete_photo') {
                cls = 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
            } else if (resolution === 'deleted_externally') {
                cls = 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
            } else if (resolution === 'overturned') {
                cls = 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300';
            }
            return { label, cls };
        },

        accountStatusLabel(status) {
            if (status === 'deleted') {
                return 'Deleted account';
            }
            if (status === 'frozen') {
                return 'Suspended account';
            }
            return 'Active account';
        },

        relTime(iso) {
            if (!iso) {
                return '';
            }
            const then = Date.parse(iso);
            if (Number.isNaN(then)) {
                return '';
            }
            const diff = Math.max(0, Date.now() - then);
            const mins = Math.floor(diff / 60000);
            if (mins < 1) {
                return 'just now';
            }
            if (mins < 60) {
                return mins + 'm ago';
            }
            const hours = Math.floor(mins / 60);
            if (hours < 24) {
                return hours + 'h ago';
            }
            const days = Math.floor(hours / 24);
            if (days < 30) {
                return days + 'd ago';
            }
            return this.formatDate(iso);
        },

        formatDate(iso) {
            if (!iso) {
                return '';
            }
            const ts = Date.parse(iso);
            if (Number.isNaN(ts)) {
                return '';
            }
            // UTC, like every other timestamp a moderator reads. Rendering this
            // in the viewer's zone while the hover tooltip on the same element
            // reads UTC would let one stored instant show two calendar days.
            return new Date(ts).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                timeZone: 'UTC',
            });
        },

        formatDateTime(iso) {
            if (!iso) {
                return '';
            }
            const ts = Date.parse(iso);
            if (Number.isNaN(ts)) {
                return '';
            }
            return new Date(ts).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'UTC',
            });
        },

        /**
         * Exact time for the hover tooltip behind a relative timestamp.
         *
         * Renders UTC in the same `2026-08-05 14:23:11 UTC` shape as the
         * server-rendered tooltips (app/Service/Web/Views/components/mod-timestamp.twig),
         * so an exact time reads identically on every moderation surface and a
         * moderator correlating a case against the mod log is comparing the
         * same numbers.
         *
         * Returns '' for a missing or unparseable value. Alpine only drops an
         * attribute for false/null/undefined, so a caller binding this must
         * write `:title="exactTime(x) || false"` to avoid an empty title.
         */
        exactTime(iso) {
            if (!iso) {
                return '';
            }
            const ts = Date.parse(iso);
            if (Number.isNaN(ts)) {
                return '';
            }
            return new Date(ts).toISOString().slice(0, 19).replace('T', ' ') + ' UTC';
        },

        /**
         * Decode a flag's JSON payload into ordered { k, v } rows for the
         * two-column key/value grid. Nested objects/arrays render as compact
         * JSON. Returns null when the flag has no JSON, it does not parse, it is
         * not an object, or it has no entries, so the caller falls back to the
         * raw string (or renders nothing for an empty payload).
         *
         * Bare JSON integers beyond Number.MAX_SAFE_INTEGER (our ids) would
         * silently corrupt through JSON.parse, so long digit runs are quoted
         * before parsing. This is a display-only transformation.
         *
         * Memoized on the payload string: the template evaluates this in x-if
         * and x-for on every reactive tick. Keying on the JSON itself (not the
         * flag id) stays correct when a flag's payload is rewritten in place
         * (device-cluster dismissals write dismissed_at into it) — a changed
         * payload is simply a new cache entry. Payloads are small and few, so
         * the cache stays negligible without a reset.
         */
        flagPairs(flag) {
            if (!flag || !flag.json) {
                return null;
            }
            this._flagPairsCache = this._flagPairsCache || {};
            if (Object.prototype.hasOwnProperty.call(this._flagPairsCache, flag.json)) {
                return this._flagPairsCache[flag.json];
            }
            const safeJson = flag.json.replace(/([:,[]\s*)(-?\d{16,})(?=\s*[,}\]])/g, '$1"$2"');
            let obj;
            let pairs = null;
            try {
                obj = JSON.parse(safeJson);
            } catch (_e) {
                obj = null;
            }
            if (obj !== null && typeof obj === 'object') {
                const entries = Object.entries(obj).map(([k, v]) => ({
                    k,
                    v: (v !== null && typeof v === 'object') ? JSON.stringify(v) : String(v),
                }));
                pairs = entries.length ? entries : null;
            }
            this._flagPairsCache[flag.json] = pairs;
            return pairs;
        },
    };
}
